import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { useCallback, useEffect, useRef, useState } from "react";

export interface IUseFFmpegAudioProps {
  coreBaseURL?: string;
  bitrate?: string;
}
export interface IUseFFmpegAudioResult {
  isReady: boolean;
  mergeBlobsToMp3: (blobs: Blob[]) => Promise<Blob>;
}
export const useFFmpegAudio = (
  { bitrate, coreBaseURL }: IUseFFmpegAudioProps = { bitrate: "192k" }
): IUseFFmpegAudioResult => {
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const ffmpeg = new FFmpeg();
        if (!ffmpeg.loaded) {
          if (coreBaseURL) {
            const coreURL = await toBlobURL(
              `${coreBaseURL}/ffmpeg-core.js`,
              "text/javascript"
            );
            const wasmURL = await toBlobURL(
              `${coreBaseURL}/ffmpeg-core.wasm`,
              "application/wasm"
            );
            const workerURL = await toBlobURL(
              `${coreBaseURL}/ffmpeg-core.worker.js`,
              "text/javascript"
            );
            await ffmpeg.load({ coreURL, wasmURL, workerURL });
          } else {
            await ffmpeg.load();
          }
        }

        ffmpegRef.current = ffmpeg;
        setIsReady(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
      }
    })();
  }, [coreBaseURL]);

  const mergeBlobsToMp3 = useCallback(
    async (blobs: Blob[]) => {
      if (!isReady || !ffmpegRef.current) {
        throw new Error("FFmpeg ainda não está carregado.");
      }

      if (blobs.length === 0) {
        throw new Error("Nenhum blob fornecido para conversão.");
      }

      const ffmpeg = ffmpegRef.current;

      const inputNames: string[] = [];
      for (let i = 0; i < blobs.length; i++) {
        const blob = blobs[i];
        const mime = blob.type || "audio/webm";
        const ext = mime.split("/")[1]?.split(";")[0] || "webm";
        const name = `in${i}.${ext}`;
        await ffmpeg.writeFile(name, await fetchFile(blob));
        inputNames.push(name);
      }

      const filterInputs = inputNames.map((_, idx) => `[${idx}:a]`).join("");
      const filter = `${filterInputs}concat=n=${inputNames.length}:v=0:a=1[a]`;

      const args = [
        ...inputNames.flatMap((n) => ["-i", n]),
        "-filter_complex",
        filter,
        "-map",
        "[a]",
        "-c:a",
        "libmp3lame",
        "-b:a",
        bitrate,
        "out.mp3",
      ] as string[];

      const ret = await ffmpeg.exec(args);
      if (ret !== 0) throw new Error(`ffmpeg retornou código ${ret}`);

      const out = await ffmpeg.readFile("out.mp3");
      const arrayBuffer = typeof out == "string" ? out : out.buffer;
      const mp3Blob = new Blob([arrayBuffer as ArrayBuffer], {
        type: "audio/mpeg",
      });

      await Promise.all(inputNames.map((n) => ffmpeg.deleteFile(n)));
      await ffmpeg.deleteFile("out.mp3");

      return mp3Blob;
    },
    [isReady, bitrate]
  );

  return { isReady, mergeBlobsToMp3 };
};
